import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectOdometryWaypointSpacing } from "@/store/view-options/view-options-selectors";
import { setOdometryWaypointSpacing } from "@/store/view-options/view-options-slice";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import { useEffect, type JSX } from "react";
import { ViewOptionsSlider } from "./view-options-slider";

/** Marks for the odometry waypoint spacing slider, from 2 m to 10 m */
const MARKS = Array.from({ length: 9 }, (_, i) => ({ value: i + 2 }));

/** @returns a slider to select the minimum spacing between odometry waypoints. */
export function OdometryWaypointSpacingSlider(): JSX.Element {
  const odometryWaypointMinimumSpacing = useAppSelector(
    selectOdometryWaypointSpacing,
  );
  const dispatch = useAppDispatch();

  // Synchronize the odometry waypoint spacing with the local storage of the browser
  const [
    localStorageOdometryWaypointSpacing,
    setLocalStorageOdometryWaypointSpacing,
  ] = useLocalStorage(
    "odometryWaypointSpacing",
    odometryWaypointMinimumSpacing,
  );
  useEffect(() => {
    if (
      odometryWaypointMinimumSpacing !== localStorageOdometryWaypointSpacing
    ) {
      dispatch(setOdometryWaypointSpacing(localStorageOdometryWaypointSpacing));
    }
  }, [
    dispatch,
    localStorageOdometryWaypointSpacing,
    odometryWaypointMinimumSpacing,
  ]);

  return (
    <ViewOptionsSlider
      label="Minimum odometry waypoint spacing"
      title="Minimum Odometry Waypoint Spacing"
      description="Sets the minimum distance between generated odometry waypoints. For example, 2 m keeps neighboring waypoints at least 2 m apart."
      step={1}
      min={2}
      max={MARKS.length + 1}
      marks={MARKS}
      value={odometryWaypointMinimumSpacing}
      valueLabelDisplay="auto"
      onChange={(_, value) => {
        if (Array.isArray(value)) {
          return;
        }
        setLocalStorageOdometryWaypointSpacing(value);
        dispatch(setOdometryWaypointSpacing(value));
      }}
    />
  );
}
